<!--
/**
 * OrangeHRM WhatsApp Integration page
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        WhatsApp Integration
      </oxd-text>
      <oxd-divider />

      <div class="orangehrm-information-card-container" style="margin-bottom: 1rem">
        <oxd-text tag="p" style="font-weight: 600; margin-bottom: 0.5rem">
          How to link your WhatsApp
        </oxd-text>
        <oxd-text tag="p" v-if="companyNumber" style="margin-bottom: 0.75rem">
          Company WhatsApp number:
          <span style="font-weight: 700; color: #1b5e20">{{ companyNumber }}</span>
        </oxd-text>
        <oxd-text tag="p" v-else style="margin-bottom: 0.75rem; color: #64728c">
          Company WhatsApp number is not set yet. Ask Admin to set it under
          WhatsApp → Configuration → Business WhatsApp Number.
        </oxd-text>
        <oxd-text tag="p">
          1) Enter your WhatsApp number below (default +91 for India — change
          country code in the same field if needed, e.g. +971…)
        </oxd-text>
        <oxd-text tag="p">
          2) Click Start linking
        </oxd-text>
        <oxd-text tag="p">
          3) Open WhatsApp and send
          <strong>Hi</strong>
          to
          <strong>{{ companyNumber || 'the company business number' }}</strong>
        </oxd-text>
        <oxd-text tag="p">
          4) Enter the OTP you receive → Verify OTP
        </oxd-text>
      </div>

      <oxd-form :loading="isLoading" @submitValid="onVerify">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="whatsappNumber"
                label="Your WhatsApp Number"
                :rules="rules.whatsappNumber"
                placeholder="+91 9876543210"
                required
                :disabled="verified && !changing"
              />
              <oxd-text tag="p" style="color: #64728c; font-size: 0.85rem; margin-top: 0.35rem">
                India default: +91 &nbsp;|&nbsp; UAE: +971 &nbsp;|&nbsp; UK: +44 —
                keep country code and number in this one field.
              </oxd-text>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                :model-value="verified ? 'Verified' : 'Not Verified'"
                label="Verification Status"
                disabled
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row v-if="linkHint">
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text tag="p" style="color: #64728c">
                {{ linkHint }}
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row v-if="otpSent">
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="otp"
                label="OTP"
                :rules="rules.otp"
                placeholder="Enter 6-digit OTP from WhatsApp"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            v-if="verified && !changing"
            type="button"
            display-type="ghost"
            label="Change Number"
            @click="onChangeNumber"
          />
          <oxd-button
            v-if="!otpSent"
            type="button"
            display-type="secondary"
            label="Start linking"
            :disabled="isLoading"
            @click="onSendOtp"
          />
          <oxd-button
            v-if="otpSent"
            display-type="secondary"
            label="Verify OTP"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {
  required,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';

const DEFAULT_NUMBER = '+91 ';

export default {
  name: 'WhatsAppIntegration',
  props: {
    empNumber: {
      type: Number,
      required: true,
    },
    displayNumber: {
      type: String,
      default: '',
    },
  },
  setup() {
    const registrationHttp = new APIService(
      window.appGlobal.baseUrl,
      'api/v2/whatsapp/registration',
    );
    const sendOtpHttp = new APIService(
      window.appGlobal.baseUrl,
      'api/v2/whatsapp/registration/send-otp',
    );
    const verifyOtpHttp = new APIService(
      window.appGlobal.baseUrl,
      'api/v2/whatsapp/registration/verify-otp',
    );
    return {registrationHttp, sendOtpHttp, verifyOtpHttp};
  },
  data() {
    return {
      isLoading: false,
      whatsappNumber: DEFAULT_NUMBER,
      companyNumber: this.displayNumber || '',
      otp: '',
      otpSent: false,
      verified: false,
      changing: false,
      linkHint: '',
      rules: {
        whatsappNumber: [required, shouldNotExceedCharLength(20)],
        otp: [required, shouldNotExceedCharLength(8)],
      },
    };
  },
  created() {
    this.loadStatus();
  },
  methods: {
    normalizeNumber(value) {
      let v = String(value || '').trim();
      if (!v || v === '+' || v === '+91') {
        return DEFAULT_NUMBER;
      }
      const digits = v.replace(/\D+/g, '');
      if (digits.length === 10) {
        return '+91 ' + digits;
      }
      if (digits && !v.startsWith('+')) {
        return '+' + digits;
      }
      return v;
    },
    loadStatus() {
      this.isLoading = true;
      this.registrationHttp
        .request({method: 'GET'})
        .then((response) => {
          const data = response.data?.data || {};
          this.whatsappNumber = data.whatsappNumber
            ? this.normalizeNumber(data.whatsappNumber)
            : DEFAULT_NUMBER;
          this.companyNumber =
            data.displayNumber || this.displayNumber || this.companyNumber || '';
          this.verified = !!data.verified;
          this.otpSent = false;
          this.changing = false;
          this.linkHint = '';
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onChangeNumber() {
      this.changing = true;
      this.verified = false;
      this.otpSent = false;
      this.otp = '';
      this.linkHint = '';
      this.whatsappNumber = DEFAULT_NUMBER;
    },
    onSendOtp() {
      this.whatsappNumber = this.normalizeNumber(this.whatsappNumber);
      if (!this.whatsappNumber || this.whatsappNumber === DEFAULT_NUMBER) {
        return;
      }
      this.isLoading = true;
      this.sendOtpHttp
        .create({whatsappNumber: this.whatsappNumber})
        .then((response) => {
          const data = response.data?.data || {};
          this.otpSent = true;
          const company = this.companyNumber || 'the company business number';
          this.linkHint =
            data.message ||
            `Now open WhatsApp and send Hi to ${company}. You will receive the OTP there.`;
          this.$toast.success({
            title: 'Next step',
            message: this.linkHint,
          });
        })
        .catch((error) => {
          const message =
            error?.data?.error?.message ||
            error?.response?.data?.error?.message ||
            'Failed to start linking';
          this.$toast.error({title: 'Error', message});
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onVerify() {
      this.whatsappNumber = this.normalizeNumber(this.whatsappNumber);
      this.isLoading = true;
      this.verifyOtpHttp
        .create({
          whatsappNumber: this.whatsappNumber,
          otp: this.otp,
        })
        .then(() => {
          this.$toast.success({
            title: 'Success',
            message: 'WhatsApp linked successfully',
          });
          this.loadStatus();
        })
        .catch((error) => {
          const message =
            error?.data?.error?.message ||
            error?.response?.data?.error?.message ||
            'OTP verification failed';
          this.$toast.error({title: 'Error', message});
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
